import React from "react";
import { reactLocalStorage } from "reactjs-localstorage";
import Constants from "../../../util/Constants";
import DailySalesReportAPI from "../../../api/DailySalesReportAPI";
import ApiUiController from "../../../controller/ApiUiController";
import { CSVLink } from "react-csv";
import { Button, Table } from "react-bootstrap";
import { FaFilter } from "react-icons/fa";
import FilterComplexPannel from "../../filter/FilterComplexPannel";
import moment from "moment";

class CallFeedbackBySalespersonWithLead extends React.Component {
  constructor(props) {
    super(props);

    const model = reactLocalStorage.getObject(Constants.Model);
    this.lead = model.lead;
    this.call = model.call;

    this.feedbackOptions = model.call.fields.feedback.options || [];
    this.userOptions = model.user.options || [];

    this.state = {
      tableData: [],
      exportData: [],
      inApiCall: false,
      isFilterOpen: false,
      filters: [
        {
          field: "enquiry_date",
          condition: "in",
          value: `${moment().startOf("month").format("YYYY-MM-DD")}~${moment()
            .endOf("month")
            .format("YYYY-MM-DD")}`,
          model_key: "lead",
        },
        {
          field: "date_time",
          condition: "in",
          value: `${moment()
            .startOf("month")
            .format("YYYY-MM-DD HH:mm:ss")}~${moment()
            .endOf("month")
            .format("YYYY-MM-DD HH:mm:ss")}`,
          model_key: "call",
        },
      ],
    };
  }

  componentDidMount() {
    this.fetchReport();
  }

  fetchReport = () => {
    this.setState({ inApiCall: true });

    const leadFilters = this.state.filters.filter(
      f => f.model_key === "lead"
    );

    const callFilters = [
      ...this.state.filters.filter(f => f.model_key === "call"),
      {
        field: "complete",
        condition: "not-in",
        value: "",
        model_key: "call",
      },
    ];
    const leadBody = {
      filters: leadFilters,
      model: "lead_model",
      x: "responsible_person",
      y: "",
      z: "COUNT(id)",
    };
    const callFeedbackBody = {
      filters: callFilters,
      model: "call_model",
      x: "feedback",
      y: "responsible_person",
      z: "COUNT(cl.id)",
    };
    const callDurationBody = {
      filters: callFilters,
      model: "call_model",
      x: "responsible_person",
      y: "",
      z: "SUM(duration)",
    };
    const callStatusBody = {
      filters: callFilters,
      model: "call_model",
      x: "call_status",
      y: "responsible_person",
      z: "COUNT(cl.id)",
    };
    const totalCallsBody = {
      filters: callFilters,
      model: "call_model",
      x: "responsible_person",
      y: "",
      z: "COUNT(cl.id)",
    };

    const makeAPICall = body =>
      new Promise((resolve, reject) =>
        DailySalesReportAPI.getFixedReport(body, resolve, reject)
      );

    Promise.all([
      makeAPICall(leadBody),
      makeAPICall(callFeedbackBody),
      makeAPICall(callDurationBody),
      makeAPICall(callStatusBody),
      makeAPICall(totalCallsBody)
    ])
      .then(
        ([
          leadData,
          callFeedbackData,
          callDurationData,
          callStatusData,
          totalCallsData
        ]) => {
          this.processData(
            leadData,
            callFeedbackData,
            callDurationData,
            callStatusData,
            totalCallsData
          );
        }
      )
      .catch(() => this.setState({ inApiCall: false }));
  };

  extractUserIds = value => {
    if (!value) return [];
    if (typeof value === "string")
      return value.split("~").filter(Boolean).map(Number);
    if (typeof value === "number") return [value];
    if (Array.isArray(value)) return value.map(Number);
    return [];
  };

  processData = (
    leadData,
    callFeedbackData,
    callDurationData,
    callStatusData,
    totalCallsData
  ) => {
    const userDataMap = {};

    this.userOptions.forEach(u => {
      userDataMap[u.value] = {
        salesperson: u.label,
        leads: 0,
        calls: 0,
        connected: 0,
        notConnected: 0,
        totalTime: 0,
        feedback: {},
      };

      this.feedbackOptions.forEach(f => {
        userDataMap[u.value].feedback[f.label] = 0;
      });
    });

    leadData?.forEach(i => {
      this.extractUserIds(i.responsible_person).forEach(uid => {
        if (userDataMap[uid]) {
          userDataMap[uid].leads += parseInt(i.sum) || 0;
        }
      });
    });

    totalCallsData?.forEach(i => {
      const callCount = parseInt(i.sum) || 0;
      this.extractUserIds(i.responsible_person).forEach(uid => {
        if (userDataMap[uid]) {
          userDataMap[uid].calls += callCount;
        }
      });
    });

    callFeedbackData?.forEach(i => {
      const feedbackValue = i.feedback?.toString();
      const callCount = parseInt(i.sum) || 0;

      if (!feedbackValue) return;

      this.extractUserIds(i.responsible_person).forEach(uid => {
        if (!userDataMap[uid]) return;

        const feedbackOption = this.feedbackOptions.find(
          f => f.value.toString() === feedbackValue
        );
        
        if (feedbackOption) {
          userDataMap[uid].feedback[feedbackOption.label] += callCount;
        }
      });
    });

    callDurationData?.forEach(i => {
      const duration = parseInt(i.sum) || 0;
      this.extractUserIds(i.responsible_person).forEach(uid => {
        if (userDataMap[uid]) {
          userDataMap[uid].totalTime += duration;
        }
      });
    });

    callStatusData?.forEach(i => {
      const status = parseInt(i.call_status);
      const callCount = parseInt(i.sum) || 0;

      this.extractUserIds(i.responsible_person).forEach(uid => {
        if (!userDataMap[uid]) return;

        if (status === 2) {
          userDataMap[uid].connected += callCount;
        } else if (status === 1) {
          userDataMap[uid].notConnected += callCount;
        }
      });
    });

    const tableData = Object.values(userDataMap).map(r => ({
      salesperson: r.salesperson,
      leads: r.leads,
      calls: r.calls, // This now shows total completed calls
      connected: r.connected,
      notConnected: r.notConnected,
      totalTime: r.totalTime,
      ...r.feedback,
    }));

    const totalRow = {
      salesperson: "TOTAL",
      leads: 0,
      calls: 0,
      connected: 0,
      notConnected: 0,
      totalTime: 0,
    };

    this.feedbackOptions.forEach(f => {
      totalRow[f.label] = 0;
    });

    tableData.forEach(r => {
      totalRow.leads += r.leads;
      totalRow.calls += r.calls;
      totalRow.connected += r.connected;
      totalRow.notConnected += r.notConnected;
      totalRow.totalTime += r.totalTime;

      this.feedbackOptions.forEach(f => {
        totalRow[f.label] += r[f.label] || 0;
      });
    });

    tableData.push(totalRow);

    this.setState({
      tableData,
      exportData: tableData,
      inApiCall: false,
    });
  };

  toggleFilter = () => {
    this.setState({ isFilterOpen: !this.state.isFilterOpen });
  };

  onFilterChange = filters => {
    this.setState({ filters }, this.fetchReport);
  };

  formatTime = s => {
    if (!s) return "0s";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h) return `${h}h ${m}m`;
    if (m) return `${m}m ${sec}s`;
    return `${sec}s`;
  };

  render() {
    const { tableData, exportData, isFilterOpen, filters, inApiCall } =
      this.state;

    return (
      <ApiUiController inApiCall={inApiCall}>
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-md-8">
              <h3>Call Report By sales Executive</h3>
            </div>
            <div className="col-md-4 text-right">
              <CSVLink
                data={exportData}
                filename="salesperson_call_feedback_report.csv"
              >
                <Button className="btn-success">Export</Button>
              </CSVLink>

              <Button
                className="btn btn-sm btn-lead ml-2"
                onClick={this.toggleFilter}
              >
                <FaFilter />
                <span className="badge badge-danger ml-1">
                  {filters.length}
                </span>
              </Button>
            </div>
          </div>

          <div className="row">
            <div className={`col-md-${isFilterOpen ? 9 : 12}`}>
              <div className="row">
                <div className="col-md-12">
                  <Table bordered hover responsive className="text-center">
                    <thead className="thead-dark">
                      <tr>
                        <th className="text-left">Salesperson</th>
                        <th>Leads</th>
                        <th>Calls<br/></th>
                        <th>Connected</th>
                        <th>Not Connected</th>
                        <th>Total Time</th>
                        {this.feedbackOptions.map(f => (
                          <th key={f.value}>{f.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((r, i) => (
                        <tr
                          key={i}
                          style={{
                            fontWeight:
                              r.salesperson === "TOTAL" ? "bold" : "normal",
                            background:
                              r.salesperson === "TOTAL"
                                ? "#f3f3f3"
                                : "transparent",
                          }}
                        >
                          <td className="text-left">{r.salesperson}</td>
                          <td>{r.leads}</td>
                          <td>{r.calls}</td>
                          <td className="text-success">{r.connected}</td>
                          <td className="text-danger">{r.notConnected}</td>
                          <td>{this.formatTime(r.totalTime)}</td>
                          {this.feedbackOptions.map(f => (
                            <td key={f.value}>{r[f.label] || 0}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>

            {isFilterOpen && (
              <FilterComplexPannel
                filters={filters}
                modelX={this.lead}
                modelY={this.call}
                onFilterChange={this.onFilterChange}
                isFilterOpen={isFilterOpen}
              />
            )}
          </div>
        </div>
      </ApiUiController>
    );
  }
}

export default CallFeedbackBySalespersonWithLead;